import { useState, useCallback } from 'react';
import { formatSocialCount } from '@/mocks/rfwSocial';

interface LikeButtonProps {
  entityId: string | number;
  entityType?: string;
  initialLikes?: number;
  initialLiked?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onToggle?: (liked: boolean) => void;
  className?: string;
}

export default function LikeButton({
  entityId,
  entityType = 'post',
  initialLikes = 0,
  initialLiked = false,
  size = 'md',
  onToggle,
  className = '',
}: LikeButtonProps) {
  const storageKey = `rfw_like_${entityType}_${entityId}`;
  const [liked, setLiked] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved !== null ? saved === 'true' : initialLiked;
    } catch {
      return initialLiked;
    }
  });
  const [likes, setLikes] = useState(initialLikes);
  const [animating, setAnimating] = useState(false);

  const handleToggle = useCallback(() => {
    const next = !liked;
    setLiked(next);
    setLikes((prev) => (next ? prev + 1 : prev - 1));
    if (next) {
      setAnimating(true);
      setTimeout(() => setAnimating(false), 400);
    }
    try { localStorage.setItem(storageKey, String(next)); } catch { /* noop */ }
    onToggle?.(next);
  }, [liked, storageKey, onToggle]);

  const sizeClasses = {
    sm: 'text-xs gap-0.5 px-1.5 py-0.5',
    md: 'text-sm gap-1 px-2 py-1',
    lg: 'text-base gap-1.5 px-3 py-1.5',
  };

  return (
    <button
      onClick={handleToggle}
      className={`inline-flex items-center cursor-pointer transition-colors whitespace-nowrap rounded-full ${sizeClasses[size]} ${
        liked ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-foreground-500 hover:text-red-500 hover:bg-background-100'
      } ${className}`}
    >
      <span className={`relative ${animating ? 'animate-bounce' : ''}`}>
        <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-lg' : 'text-sm'}`}></i>
      </span>
      <span className="font-medium">{formatSocialCount(likes)}</span>
    </button>
  );
}